import { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ClientInfo {
  firstName: string;
  lastName: string;
  email: string;
}

// Rémi's profile image URL
const REMI_AVATAR = "https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/Profile Images/8f804e22-b723-42ea-be44-518cd2833274/a508433e-b19e-4827-ac93-95100c77cb2c.jpeg";

// Parse message content and extract quote links
const parseMessageContent = (content: string) => {
  const parts: Array<{ type: 'text' | 'link'; content: string; url?: string }> = [];
  
  const quoteLinkRegex = /\[QUOTE_LINK:([^\]]+)\]/g;
  let lastIndex = 0;
  let match;

  while ((match = quoteLinkRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'link', content: 'Voir mon devis', url: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text' as const, content }];
};

// Format text with basic markdown (bold)
const formatText = (text: string) => {
  return text.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Show bubble after a delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowBubble(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Hide bubble when chat is open
  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
    }
  }, [isOpen]);

  // Real-time subscription for admin replies
  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel(`chatbot-messages-${conversationId}`)
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'chatbot_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          const newMsg = payload.new as { id: string; role: string; content: string; created_at: string };
          // Only add if it's an assistant message (admin reply) and not already in messages
          if (newMsg.role === 'assistant') {
            setMessages(prev => {
              // Check if message already exists
              if (prev.some(m => m.content === newMsg.content && 
                Math.abs(new Date(m.content).getTime() - new Date(newMsg.created_at).getTime()) < 5000)) {
                return prev;
              }
              // Avoid exact duplicates
              const lastMsg = prev[prev.length - 1];
              if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content === newMsg.content) {
                return prev;
              }
              return [...prev, { role: 'assistant', content: newMsg.content }];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  // Presence tracking - let admin know when client is online
  useEffect(() => {
    if (!conversationId || !isOpen) return;

    const presenceChannel = supabase.channel('chatbot-presence');
    
    presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await presenceChannel.track({
          conversation_id: conversationId,
          online_at: new Date().toISOString()
        });
      }
    });

    return () => {
      presenceChannel.untrack();
      supabase.removeChannel(presenceChannel);
    };
  }, [conversationId, isOpen]);

  // Save message to database
  const saveMessage = async (convId: string, role: 'user' | 'assistant', content: string) => {
    try {
      await supabase.from('chatbot_messages').insert({
        conversation_id: convId,
        role,
        content
      });
      
      await supabase
        .from('chatbot_conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', convId);
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const createClientRecord = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return;
    }

    setIsCreatingClient(true);
    try {
      const clientEmail = email.toLowerCase().trim();
      const clientFirstName = firstName.trim();
      const clientLastName = lastName.trim();

      // Check if client already exists
      const { data: existingClient, error: checkError } = await supabase
        .from('clients')
        .select('id')
        .eq('email', clientEmail)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking client:', checkError);
      }

      if (!existingClient) {
        const { error: insertError } = await supabase
          .from('clients')
          .insert({
            first_name: clientFirstName,
            last_name: clientLastName,
            email: clientEmail,
            source: 'chatbot',
            notes: 'Client créé via le chatbot'
          });
        
        if (insertError) {
          console.error('Error creating client:', insertError);
          // Continue anyway - client creation is optional
        }
      }

      // Create conversation
      const { data: conversation, error: convError } = await supabase
        .from('chatbot_conversations')
        .insert({
          client_email: clientEmail,
          client_first_name: clientFirstName,
          client_last_name: clientLastName,
          status: 'active'
        })
        .select()
        .single();

      if (convError) {
        console.error('Error creating conversation:', convError);
        throw new Error('Erreur lors de la création de la conversation');
      }
      
      setConversationId(conversation.id);

      // Send push notification to admins (non-blocking)
      fetch(`https://kxrduwrurqjvouiqobyc.supabase.co/functions/v1/send-push-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt4cmR1d3J1cnFqdm91aXFvYnljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUwMTEzOTcsImV4cCI6MjA2MDU4NzM5N30.p8YjQNTTWTZ91lzhLgyUfFKwFnY8LrSyuiat9vh2zYY`
        },
        body: JSON.stringify({
          title: '💬 Nouvelle conversation chatbot',
          body: `${clientFirstName} ${clientLastName} a démarré une conversation`,
          url: '/admin?tab=chatbot',
          tag: `chatbot-${conversation.id}`
        })
      }).catch(err => console.log('Push notification skipped:', err));

      setClientInfo({
        firstName: clientFirstName,
        lastName: clientLastName,
        email: clientEmail
      });

      const welcomeMessage = `Bonjour ${clientFirstName} ! 👋 Je suis l'assistant virtuel de la Villa Douce Vague. Comment puis-je vous aider aujourd'hui ? Je peux répondre à vos questions sur la villa, vous aider à planifier votre séjour ou créer un devis personnalisé.`;
      
      setMessages([{ role: 'assistant', content: welcomeMessage }]);
      await saveMessage(conversation.id, 'assistant', welcomeMessage);

    } catch (error) {
      console.error('Error in createClientRecord:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la connexion');
    } finally {
      setIsCreatingClient(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading || !clientInfo || !conversationId) return;

    const userMessage: Message = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    await saveMessage(conversationId, 'user', userMessage.content);

    let assistantContent = '';

    try {
      const response = await fetch(`https://kxrduwrurqjvouiqobyc.supabase.co/functions/v1/chatbot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt4cmR1d3J1cnFqdm91aXFvYnljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUwMTEzOTcsImV4cCI6MjA2MDU4NzM5N30.p8YjQNTTWTZ91lzhLgyUfFKwFnY8LrSyuiat9vh2zYY`
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({ role: m.role, content: m.content })),
          clientInfo
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur de communication');
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
                return updated;
              });
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      if (assistantContent) {
        await saveMessage(conversationId, 'assistant', assistantContent);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = 'Désolé, une erreur est survenue. Veuillez réessayer.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
      await saveMessage(conversationId, 'assistant', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderMessageContent = (content: string) => {
    const parts = parseMessageContent(content);
    
    return (
      <div className="space-y-2">
        {parts.map((part, index) => {
          if (part.type === 'link' && part.url) {
            return (
              <Button
                key={index}
                variant="secondary"
                size="sm"
                className="w-full mt-2"
                onClick={() => window.open(part.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {part.content}
              </Button>
            );
          }
          return (
            <p key={index} className="text-sm whitespace-pre-wrap">
              {formatText(part.content)}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {/* Floating Button with Avatar and Speech Bubble */}
      <div className={`fixed bottom-6 right-6 z-50 flex items-end gap-3 transition-all duration-300 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>
        {/* Speech Bubble */}
        <div 
          className={`bg-white rounded-2xl rounded-br-sm shadow-lg px-4 py-3 max-w-[200px] relative transition-all duration-500 ${
            showBubble ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}
        >
          <p className="text-sm font-medium text-villa-blue">Une question ? 💬</p>
          <p className="text-xs text-muted-foreground mt-0.5">Je suis là pour vous aider !</p>
          <div className="absolute -right-2 bottom-3 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-white"></div>
        </div>
        
        {/* Avatar Button */}
        <button
          onClick={() => setIsOpen(true)}
          className="relative w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group overflow-hidden ring-4 ring-villa-turquoise/30 hover:ring-villa-turquoise/50 bg-villa-sand"
          aria-label="Ouvrir le chat"
        >
          <img 
            src={REMI_AVATAR} 
            alt="Assistant Rémi" 
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement?.classList.add('flex', 'items-center', 'justify-center', 'text-white', 'font-bold', 'text-xl', 'bg-villa-turquoise');
              e.currentTarget.parentElement!.innerHTML = '<span>R</span>';
            }}
          />
          <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full animate-pulse"></span>
        </button>
      </div>

      {/* Chat Window */}
      <div className={`fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-background border border-border rounded-2xl shadow-2xl transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-villa-blue to-villa-turquoise text-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white/30 bg-white/20 flex items-center justify-center">
              <img 
                src={REMI_AVATAR} 
                alt="Rémi" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span className="text-white font-semibold">R</span>
            </div>
            <div>
              <h3 className="font-semibold">Rémi</h3>
              <p className="text-xs opacity-80">Assistant Villa Douce Vague</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="h-96 overflow-y-auto p-4">
          {!clientInfo ? (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h4 className="font-semibold text-lg mb-2">Bienvenue ! 👋</h4>
                <p className="text-sm text-muted-foreground">
                  Pour commencer, merci de vous présenter
                </p>
              </div>
              
              <div className="space-y-3">
                <Input
                  placeholder="Prénom *"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isCreatingClient}
                />
                <Input
                  placeholder="Nom *"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isCreatingClient}
                />
                <Input
                  type="email"
                  placeholder="Email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isCreatingClient}
                />
              </div>

              <Button 
                className="w-full" 
                onClick={createClientRecord}
                disabled={isCreatingClient || !firstName.trim() || !lastName.trim() || !email.trim()}
              >
                {isCreatingClient ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Commencer la discussion'
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Vos informations nous permettent de vous recontacter si nécessaire
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-muted text-foreground rounded-bl-md'
                    }`}
                  >
                    {renderMessageContent(message.content)}
                  </div>
                </div>
              ))}
              {isLoading && messages[messages.length - 1]?.role === 'user' && (
                <div className="flex justify-start">
                  <div className="bg-muted p-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        {clientInfo && (
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                placeholder="Votre message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                size="icon"
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatBot;